import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import {
  fetchCampaignLastActivity,
  downloadCsv,
  CampaignLastActivityResponse,
  CampaignLastActivityRow,
} from '~/services/predefinedReports';
import { PredefinedResultsLayout, Column } from './PredefinedResultsLayout';

// Dates arrive as plain YYYY-MM-DD; parsing at local midnight keeps them from
// sliding a day back in negative-offset timezones.
const formatDate = (value: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString() : '—';

const columns: Column<CampaignLastActivityRow>[] = [
  {
    // The report is a worklist: staff run it, then open the stale campaigns to
    // log the outreach. A new tab keeps the list they were working (HHLTB-2125).
    key: 'view',
    label: '',
    render: (r) => (
      <Link
        to={`/campaigns/${r.campaign_id}`}
        target='_blank'
        rel='noopener noreferrer'
        title='View Campaign Details'
        className='inline-flex h-8 w-8 items-center justify-center rounded text-primary transition-colors duration-200 hover:bg-primary/10 hover:text-primary/80'
      >
        <FaEye size={16} />
      </Link>
    ),
  },
  { key: 'ntaf_id', label: 'NTAF ID' },
  { key: 'campaign_name', label: 'Campaign' },
  { key: 'status', label: 'Status' },
  { key: 'csc_name', label: 'CSC' },
  { key: 'client_email', label: 'Client Email' },
  { key: 'primary_contact_email', label: 'Primary Contact Email' },
  { key: 'last_activity', label: 'Last Activity', render: (r) => formatDate(r.last_activity) },
  { key: 'last_activity_type', label: 'Activity Type' },
  {
    key: 'days_since_activity',
    label: 'Days Since',
    rightAlign: true,
    render: (r) => (r.days_since_activity === null ? '—' : r.days_since_activity),
  },
  { key: 'last_donation', label: 'Donation', render: (r) => formatDate(r.last_donation) },
  { key: 'last_update', label: 'Update', render: (r) => formatDate(r.last_update) },
  { key: 'last_guestbook', label: 'Guestbook', render: (r) => formatDate(r.last_guestbook) },
  { key: 'last_message', label: 'Message', render: (r) => formatDate(r.last_message) },
  { key: 'last_fund_request', label: 'Fund Request', render: (r) => formatDate(r.last_fund_request) },
];

const CampaignLastActivityResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<CampaignLastActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = {
      inactive_since: searchParams.get('inactive_since') || undefined,
      campaign_status: searchParams.get('campaign_status') || undefined,
      csc: searchParams.get('csc') || undefined,
    };
    setLoading(true);
    fetchCampaignLastActivity(params)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { setError(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [searchParams]);

  const subtitle = data?.inactive_since
    ? `No activity since ${formatDate(data.inactive_since)}, stalest first`
    : 'All campaigns in scope, stalest first';

  return (
    <PredefinedResultsLayout
      title='Campaign Last Activity'
      subtitle={subtitle}
      columns={columns}
      rows={data?.rows ?? []}
      loading={loading}
      error={error}
      rowKey={(r) => r.campaign_id}
      onDownloadCsv={() => downloadCsv('campaign-last-activity', searchParams)}
    />
  );
};

export default CampaignLastActivityResults;
